import type { WorkspaceDoc } from '@bower/shared';
import { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAppToast } from '../ui/useAppToast';
import { isWorkspaceSwitchSupersededError } from '../../utils/workspaceSwitch';
import { apiService } from '../../services/api.service';
import { updateLastLoggedInWorkspace } from '../../services/api/invitations.api';
import { getWorkspaces } from '../../services/api/workspace.api';
import { getUnsavedWorkLabels } from '../../stores/unsavedWorkStore';

/**
 * Marks the switcher's post-switch navigation. The user already confirmed
 * interrupting unsaved work here, so the note page's leave-while-recording
 * blocker lets this one through instead of asking twice.
 */
const WORKSPACE_SWITCH_NAV_STATE = { fromWorkspaceSwitch: true } as const;

export function isWorkspaceSwitchNavigation(location: { state?: unknown }): boolean {
  const { state } = location;
  return typeof state === 'object' && state !== null && 'fromWorkspaceSwitch' in state;
}

interface UseWorkspaceSwitcherOptions {
  onWorkspaceChange?: (workspace: WorkspaceDoc) => void;
}

/**
 * Workspace list + switch behaviour, extracted from WorkspaceSwitcherDropdown
 * so more than one trigger surface can share it.
 *
 * The intent/lifecycle guards are load-bearing: a fast double-switch used to
 * commit the wrong workspace, so every user-facing effect (persistence write,
 * toast, callback, navigation) is gated on the selection still being the
 * current one AND the auth identity not having changed underneath it.
 */
export function useWorkspaceSwitcher(options?: UseWorkspaceSwitcherOptions) {
  const onWorkspaceChange = options?.onWorkspaceChange;
  const { user } = useAuth();
  const { workspace, switchWorkspace } = useWorkspace();
  const toast = useAppToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [availableWorkspaces, setAvailableWorkspaces] = useState<WorkspaceDoc[]>([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const switchIntentRef = useRef(0);
  const lastWorkspaceWriteQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const lifecycleGenerationRef = useRef(0);
  const authIdentity = user?.internalId || user?.uid || null;

  useLayoutEffect(() => {
    const lifecycleGeneration = lifecycleGenerationRef.current + 1;
    lifecycleGenerationRef.current = lifecycleGeneration;

    return () => {
      switchIntentRef.current += 1;
      if (lifecycleGenerationRef.current === lifecycleGeneration) {
        lifecycleGenerationRef.current += 1;
      }
    };
  }, [authIdentity]);

  // Load available workspaces
  const loadWorkspaces = async () => {
    setIsLoadingWorkspaces(true);
    try {
      const response = await getWorkspaces(apiService);
      if (response.success && response.data) {
        let workspacesList: WorkspaceDoc[] = [];
        if (Array.isArray(response.data)) {
          workspacesList = response.data;
        } else if (
          response.data &&
          typeof response.data === 'object' &&
          Array.isArray((response.data as any).workspaces)
        ) {
          workspacesList = (response.data as any).workspaces;
        } else if (response.data && typeof response.data === 'object') {
          workspacesList = [response.data as WorkspaceDoc];
        }
        // Alphabetical, case-insensitive, so every switcher lists the same order.
        setAvailableWorkspaces(
          [...workspacesList].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }),
          ),
        );
      } else {
        setAvailableWorkspaces([]);
      }
    } catch (error) {
      console.error('Failed to load workspaces:', error);
      setAvailableWorkspaces([]);
    } finally {
      setIsLoadingWorkspaces(false);
    }
  };

  // A switch while anything is in the unsaved-work register (a recording, an
  // image upload, Image to Text) waits for confirmation: it interrupts that
  // work. Read at click time, not via a subscription, so the menu does not
  // re-render on every registration.
  const [pendingSwitch, setPendingSwitch] = useState<WorkspaceDoc | null>(null);

  const handleWorkspaceSwitch = (selectedWorkspace: WorkspaceDoc) => {
    if (getUnsavedWorkLabels().length > 0) {
      setPendingSwitch(selectedWorkspace);
      return Promise.resolve();
    }
    return performWorkspaceSwitch(selectedWorkspace);
  };

  const confirmPendingSwitch = () => {
    if (!pendingSwitch) return;
    setPendingSwitch(null);
    void performWorkspaceSwitch(pendingSwitch);
  };

  const cancelPendingSwitch = () => setPendingSwitch(null);

  // Handle workspace selection
  const performWorkspaceSwitch = async (selectedWorkspace: WorkspaceDoc) => {
    const switchIntent = switchIntentRef.current + 1;
    switchIntentRef.current = switchIntent;
    const lifecycleGeneration = lifecycleGenerationRef.current;
    const isCurrent = () =>
      switchIntent === switchIntentRef.current &&
      lifecycleGeneration === lifecycleGenerationRef.current;

    try {
      // Update workspace via context
      await switchWorkspace(selectedWorkspace.id);
      if (!isCurrent()) return;

      const persistence = lastWorkspaceWriteQueueRef.current.then(() => {
        if (lifecycleGeneration !== lifecycleGenerationRef.current) return undefined;
        return updateLastLoggedInWorkspace(apiService, selectedWorkspace.id);
      });
      lastWorkspaceWriteQueueRef.current = persistence.catch(() => undefined);

      try {
        await persistence;
      } catch (updateError) {
        if (isCurrent()) {
          console.warn('Failed to update last logged in workspace (non-critical):', updateError);
        }
      }

      if (!isCurrent()) return;

      toast({
        title: 'Workspace Switched',
        description: `Switched to ${selectedWorkspace.name}`,
        status: 'success',
        duration: 2000,
      });

      // Call optional callback
      if (onWorkspaceChange) {
        onWorkspaceChange(selectedWorkspace);
      }

      // SPA navigation — no full page reload so Firebase auth state is preserved
      // and the user never sees the login screen. queryClient.clear() (called
      // inside switchWorkspace) already ensures stale data is evicted.
      // SocketContext re-connects automatically when workspaceId changes.
      const targetPath = location.pathname.startsWith('/m/') ? location.pathname : '/dashboard';
      navigate(targetPath, { replace: true, state: WORKSPACE_SWITCH_NAV_STATE });
    } catch (error: unknown) {
      if (isWorkspaceSwitchSupersededError(error)) return;
      if (!isCurrent()) return;
      const description =
        error instanceof Error && error.message
          ? error.message
          : 'Unable to switch workspace. Please try again.';
      toast({
        title: 'Failed to Switch Workspace',
        description,
        status: 'error',
        duration: 5000,
      });
    }
  };

  return {
    workspace,
    availableWorkspaces,
    isLoadingWorkspaces,
    loadWorkspaces,
    handleWorkspaceSwitch,
    pendingSwitch,
    confirmPendingSwitch,
    cancelPendingSwitch,
  };
}
